/* Les composants, rendus par le code qui les rend ailleurs.
 *
 * Chacun est documenté de la même façon : ce qu'il montre, ce qu'il fait sous
 * la main (les états), ce qu'il prend (l'API), ce qu'il garantit déjà
 * (accessibilité). Un composant dont les états ne sont pas montrés est un
 * composant dont personne ne sait à quoi ressemble le refus.
 */

import { useState } from "react";
import {
  Bars, Capsule, Kpi, Kpis, Logo, Onglets, Section, Segment, Stepper, Tag,
} from "@/ui/primitives";
import { Icon } from "@/ui/Icon";
import { Table } from "@/ui/Table";
import { A11y, Api, BlocP, Demo, Etat, Etats, Juste } from "./briques";

interface Rangee { ref: string; nom: string; etat: string; montant: number }

const EXEMPLE: Rangee[] = [
  { ref: "SO-0042", nom: "Distrisud Méditerranée", etat: "Facturé", montant: 12480 },
  { ref: "SO-0041", nom: "Épicerie Nordique AB", etat: "Expédié", montant: 3150.4 },
  { ref: "SO-0040", nom: "Maison Perret", etat: "Devis", montant: 890 },
];

export function Composants() {
  const [segment, setSegment] = useState<string | null>("tous");
  const [onglet, setOnglet] = useState("identite");

  return (
    <>
      <Section titre="Étiquettes" note="un état, jamais une action">
        <div className="card card-pad">
          <Demo quoi="<Tag ton dot>">
            <div className="rang">
              <Tag>neutre</Tag>
              <Tag ton="ok" dot>payé</Tag>
              <Tag ton="warn" dot>en attente</Tag>
              <Tag ton="crit" dot>en retard</Tag>
              <Tag ton="info">devis</Tag>
              <Tag ton="accent">direction</Tag>
              <Tag ton="violet">packaging</Tag>
              <Tag ton="ciel">qualité</Tag>
            </div>
          </Demo>
          <Api
            lignes={[
              { nom: "ton", type: '"" | "ok" | "warn" | "crit" | "info" | "accent" | "violet" | "ciel"', defaut: '""', quoi: "la paire de couleurs ; neutre par défaut" },
              { nom: "dot", type: "boolean", defaut: "false", quoi: "une pastille devant, pour les états qui se suivent du regard" },
              { nom: "children", type: "ReactNode", quoi: "le libellé, en minuscules, sans point" },
            ]}
          />
          <Juste
            oui={{ quoi: "l'état, au présent", vue: <Tag ton="ok" dot>payé</Tag> }}
            non={{ quoi: "une action déguisée", vue: <Tag ton="accent">Marquer payé</Tag> }}
          />
          <A11y>
            La couleur ne porte jamais seule : le mot dit l'état, la teinte ne fait que le
            rappeler. Un tableau filtré sur la couleur reste lisible en niveaux de gris.
          </A11y>
        </div>
      </Section>

      <Section titre="Boutons" note="une seule action principale par écran">
        <div className="card card-pad">
          <Demo quoi="button.btn">
            <div className="rang">
              <button type="button" className="btn btn-primary">Enregistrer</button>
              <button type="button" className="btn"><Icon n="plus" cls="" />Ajouter</button>
              <button type="button" className="btn btn-sm">Petit</button>
              <button type="button" className="btn btn-ghost">Annuler</button>
            </div>
          </Demo>

          <Etats>
            <Etat nom="repos"><button type="button" className="btn btn-primary">Enregistrer</button></Etat>
            <Etat nom="survol"><button type="button" className="btn btn-primary demo-survol">Enregistrer</button></Etat>
            <Etat nom="focus clavier"><button type="button" className="btn btn-primary demo-focus">Enregistrer</button></Etat>
            <Etat nom="désactivé">
              <button type="button" className="btn btn-primary" disabled title="Choisissez d'abord une usine">
                Enregistrer
              </button>
            </Etat>
            <Etat nom="en cours"><button type="button" className="btn btn-primary" disabled>Enregistrement…</button></Etat>
          </Etats>

          <BlocP>
            Un bouton désactivé porte toujours un <code>title</code> qui dit ce qui manque. Sans
            lui, le refus est muet — et l'écran envoie chercher.
          </BlocP>
          <Juste
            oui={{
              quoi: "une seule action pleine, le reste en retrait",
              vue: (
                <div className="rang">
                  <button type="button" className="btn btn-primary">Enregistrer le devis — 450,00 €</button>
                  <button type="button" className="btn">Ajouter des références</button>
                  <button type="button" className="btn btn-ghost">Vider les lignes</button>
                </div>
              ),
            }}
            non={{
              quoi: "trois actions pleines : plus rien ne ressort",
              vue: (
                <div className="rang">
                  <button type="button" className="btn btn-primary">Enregistrer</button>
                  <button type="button" className="btn btn-primary">Ajouter</button>
                  <button type="button" className="btn btn-primary">Vider</button>
                </div>
              ),
            }}
          />
          <A11y>
            Le focus clavier est un anneau de 2 px en <code>--accent</code>, jamais retiré. Un
            bouton désactivé reste annoncé comme tel plutôt que sorti du parcours de tabulation.
          </A11y>
        </div>
      </Section>

      <Section titre="Filtres et navigation" note="pastilles au bureau, liste au téléphone">
        <div className="card card-pad">
          <Demo quoi="<Segment>" note="les deux formes sont rendues, la CSS n'en montre qu'une">
            <Segment
              options={[
                { cle: "tous", nom: "Tous", n: 42 },
                { cle: "ouverts", nom: "Ouverts", n: 12 },
                { cle: "clos", nom: "Clos", n: 30 },
              ]}
              valeur={segment}
              onChange={setSegment}
            />
          </Demo>
          <Api
            lignes={[
              { nom: "options", type: "{ cle, nom, n? }[]", quoi: "les choix ; n affiche le compte" },
              { nom: "valeur", type: "T | null", quoi: "le choix courant ; null = aucun filtre" },
              { nom: "onChange", type: "(v: T) => void", quoi: "appelé au clic comme à la sélection" },
              { nom: "label", type: "string", defaut: '"Filtrer"', quoi: "nom accessible de la liste déroulante" },
            ]}
          />

          <Demo quoi="<Onglets>" note="naviguer dans une fiche, sans changer d'adresse">
            <Onglets
              items={[
                { cle: "identite", nom: "Identité" },
                { cle: "ventes", nom: "Ventes", n: 7 },
                { cle: "packaging", nom: "Packaging", n: 2 },
              ]}
              actif={onglet}
              onChange={setOnglet}
            />
          </Demo>
          <A11y>
            Les onglets portent <code>role="tablist"</code> et <code>aria-selected</code> ; les
            pastilles de filtre portent <code>aria-pressed</code>. L'état n'est pas qu'une
            couleur.
          </A11y>
        </div>
      </Section>

      <Section titre="Saisie" note="étiquette au-dessus, jamais à l'intérieur">
        <div className="card card-pad">
          <Demo quoi="label.ch">
            <div className="ligne-champs">
              <label className="ch">
                <span>Conditionnement</span>
                <select defaultValue="a">
                  <option value="a">Sac 1 kg — 20/carton</option>
                  <option value="b">Pot 100 g — 12/carton</option>
                </select>
              </label>
              <label className="ch ch-n">
                <span>Cartons</span>
                <input type="number" defaultValue={3} />
              </label>
              <label className="ch ch-n">
                <span>Prix unitaire</span>
                <input type="number" defaultValue={7.5} step="0.01" />
              </label>
            </div>
          </Demo>

          <Etats>
            <Etat nom="repos">
              <label className="ch"><span>Référence</span><input defaultValue="AROPOT-013" /></label>
            </Etat>
            <Etat nom="focus">
              <label className="ch"><span>Référence</span><input className="demo-focus" defaultValue="AROPOT-013" /></label>
            </Etat>
            <Etat nom="hors limite">
              <label className="ch ch-n">
                <span>Prix unitaire</span>
                <input className="sous-plancher" type="number" defaultValue={0.4} />
              </label>
            </Etat>
            <Etat nom="vide">
              <label className="ch"><span>Contact</span><input placeholder="Nom de la personne" /></label>
            </Etat>
          </Etats>

          <BlocP>
            L'étiquette reste visible pendant la saisie : une étiquette posée dans le champ
            disparaît dès qu'on tape, et c'est à ce moment-là qu'on en a besoin. Au téléphone,
            tout champ fait au moins <code>--t-fort</code> (16 px) — en dessous, iOS zoome tout
            seul et la page se déplace sous le doigt.
          </BlocP>
          <A11y>
            Chaque champ est enveloppé dans son <code>label</code> : la zone cliquable couvre
            l'étiquette, et le lecteur d'écran annonce le nom sans attribut supplémentaire.
          </A11y>
        </div>
      </Section>

      <Section titre="Indicateurs et parcours">
        <div className="card card-pad">
          <Demo quoi="<Kpis> · <Kpi>" note="le chiffre, puis ce qu'il recouvre">
            <Kpis>
              <Kpi k="Lignes" v="3" n="3 cartons · 60 unités" />
              <Kpi k="Total HT" v="450,00 €" n="montant du devis" />
              <Kpi k="Marge" v={<span className="marge-ko">−2,4 %</span>} n="sous le plancher" />
            </Kpis>
          </Demo>
          <BlocP>
            Un indicateur sans sa note est un chiffre qu'on ne peut pas vérifier. La note dit d'où
            il vient — « 3 cartons · 60 unités » se recoupe, « 60 » ne se recoupe pas.
          </BlocP>

          <Demo quoi="<Stepper>" note="où en est un dossier">
            <Stepper
              etapes={["Technique", "Design", "Validation", "Production", "Reçu"]}
              courante="Validation"
              tons={["ok", "ok", "warn", "", ""]}
            />
          </Demo>

          <Demo quoi="<Bars>" note="comparer des grandeurs, sans axe inventé">
            <Bars
              data={[
                { label: "Aromasol", valeur: 48200, sous: "12 ventes" },
                { label: "Mundi Riso", valeur: 31400, sous: "8 ventes" },
                { label: "Agrover", valeur: 12750, sous: "4 ventes" },
              ]}
              format={(n) => `${n.toLocaleString("fr-FR")} €`}
              axe
            />
          </Demo>
          <A11y>
            Les barres ne sont pas qu'une longueur : chaque ligne porte sa valeur écrite, et la
            rangée entière un <code>title</code> qui la résume.
          </A11y>
        </div>
      </Section>

      <Section titre="Tableau" note="cliquable, filtrable, totalisable">
        <div className="card card-pad">
          <Demo quoi="<Table>">
            <Table
              lignes={EXEMPLE}
              cle={(l) => l.ref}
              colonnes={[
                { cle: "ref", titre: "Référence", type: "ref", sous: (l) => l.nom },
                { cle: "nom", titre: "Client", classe: "name", mobile: false },
                { cle: "etat", titre: "État", filtre: "select" },
                { cle: "montant", titre: "Montant", type: "euro", total: true, nombre: (l) => l.montant },
              ]}
            />
          </Demo>
          <Api
            lignes={[
              { nom: "lignes", type: "L[]", quoi: "les données, déjà triées" },
              { nom: "cle", type: "(l: L) => string", quoi: "l'identité d'une ligne" },
              { nom: "colonnes", type: "Colonne<L>[]", quoi: "ce qui s'affiche, et comment" },
              { nom: "onClic", type: "(l: L) => void", quoi: "ouvre la fiche ; la ligne devient cliquable au clavier" },
              { nom: "colonne.mobile", type: "false", quoi: "la colonne disparaît sous 560 px" },
              { nom: "colonne.total", type: "boolean", quoi: "une ligne de total sous le tableau" },
              { nom: "colonne.filtre", type: '"texte" | "select"', quoi: "ajoute le filtre correspondant" },
            ]}
          />
          <BlocP>
            Une colonne secondaire porte <code>mobile: false</code> plutôt que de rétrécir : sous
            560 px, mieux vaut trois colonnes lisibles que six illisibles. Quand même les trois ne
            tiennent pas, la table devient une suite de blocs.
          </BlocP>
        </div>
      </Section>

      <Section titre="Marque et identité">
        <div className="card card-pad">
          <Demo quoi="<Logo> · <Capsule>" note="l'usine à sa couleur, la marque à la sienne">
            <div className="rang" style={{ alignItems: "center" }}>
              <Logo nom="Aromasol" couleur="#B4442C" />
              <Logo nom="Mundi Riso" couleur="#2F6E86" />
              <Logo nom="Agrover S.R.L." couleur="#5B4B8A" />
              <Capsule taille={13} />
            </div>
          </Demo>
          <BlocP>
            La couleur d'une usine lui appartient : c'est la même sur sa tuile, dans les
            graphiques et sur ses documents. Elle n'est pas prise dans les rampes — elle n'a pas à
            l'être, c'est une donnée, pas une décision de charte.
          </BlocP>
        </div>
      </Section>
    </>
  );
}
